import { useEffect } from "react";
import { useImmer } from "use-immer";
import { errorMessage, successMessage } from "../../../utils";
import {
  assignProductToPointPromotionApi,
  deletePointPromotionProductApi,
  getPointPromotionProductsApi,
  getLoyaltyCardTypeDetailsApi,
} from "../api";

export const usePointPromotionProducts = ({ promotionId, promotionData }) => {
  const resolveLoyaltyCardTypeId = (cardType) => {
    if (cardType === 1 || cardType === 2) return cardType;
    if (!cardType) return null;

    const normalized = cardType.toString().toLowerCase();
    if (normalized === "customer") return 1;
    if (normalized === "sponsorship") return 2;

    return null;
  };

  const filterWithIncludedProducts = (products, includedProductIds) => {
    if (!includedProductIds?.length) return products;
    return (products || []).filter((product) =>
      includedProductIds.includes(product.id),
    );
  };

  // Nest variants under their parents; if a variant's parent is not in
  // the included list, borrow the parent shell from the excluded list.
  const buildNestedProductList = (includedCategories, excludedCategories) => {
    const cloneDeep = (obj) => JSON.parse(JSON.stringify(obj));
    const excludedCategoryMap = {};
    (excludedCategories || []).forEach((cat) => {
      excludedCategoryMap[cat.id] = cat;
    });

    return (includedCategories || []).map((category) => {
      const products = cloneDeep(category.products || []);
      const productMap = {};
      products.forEach((p) => (productMap[p.id] = p));

      const excludedCategory = excludedCategoryMap[category.id];
      const excludedProductMap = {};
      (excludedCategory?.products || []).forEach(
        (p) => (excludedProductMap[p.id] = p),
      );

      const childIdsToRemove = new Set();
      const borrowedParents = [];

      products.forEach((product) => {
        if (!product.parent_product_id) return;

        const parentId = product.parent_product_id;
        childIdsToRemove.add(product.id);

        // 1. parent already in same list
        const parent = productMap[parentId];
        if (parent) {
          if (!parent.variants) parent.variants = [];
          parent.has_variant = true;
          parent.variants.push(product);
          return;
        }

        // 2. borrow parent shell from excluded list
        let borrowed = borrowedParents.find((p) => p.id === parentId);
        if (!borrowed) {
          const excludedParent = excludedProductMap[parentId];
          if (excludedParent) {
            borrowed = cloneDeep(excludedParent);
            borrowed.variants = [];
            borrowed.has_variant = true;
            borrowedParents.push(borrowed);
          }
        }
        if (borrowed) borrowed.variants.push(product);
      });

      const finalProducts = [
        ...products.filter((p) => !childIdsToRemove.has(p.id)),
        ...borrowedParents.filter((p) => p.variants.length > 0),
      ];

      return { ...category, products: finalProducts };
    });
  };

  const [state, setState] = useImmer({
    isBusy: false,
    isDelete: false,
    isOpen: false,
    isApplyAllConfirmOpen: false,
    productId: null,
    assign: {
      productList: [],
      allProducts: [],
      filteredProducts: [],
      selectedProducts: [],
      allCategoryProductList: [],
      includedProductIds: [],
      isModalLoading: false,
      isSaveButtonBusy: false,
    },
  });

  const getPointPromotionProducts = async () => {
    if (!promotionId) {
      setState((draft) => {
        draft.assign.productList = Array.isArray(promotionData?.products)
          ? promotionData.products
          : [];
      });
      return;
    }

    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      const res = await getPointPromotionProductsApi(promotionId);
      if (res?.success) {
        const filteredProducts = filterWithIncludedProducts(
          res.data || [],
          state.assign.includedProductIds,
        );

        setState((draft) => {
          draft.assign.productList = filteredProducts;
          draft.assign.selectedProducts = filteredProducts.map((item) => item.id);
          draft.isBusy = false;
        });
      } else {
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to load products");
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  const getLoyaltyIncludedProducts = async () => {
    const loyaltyCardTypeId = resolveLoyaltyCardTypeId(promotionData?.card_type);
    if (!loyaltyCardTypeId) {
      setState((draft) => {
        draft.assign.allCategoryProductList = [];
        draft.assign.allProducts = [];
        draft.assign.filteredProducts = [];
        draft.assign.includedProductIds = [];
      });
      return;
    }

    try {
      setState((draft) => {
        draft.assign.isModalLoading = true;
      });
      const res = await getLoyaltyCardTypeDetailsApi(loyaltyCardTypeId);

      if (res?.success) {
        const includedCategories = (res.data?.included_products || []).filter(
          (category) => (category.products || []).length > 0,
        );
        const excludedCategories = res.data?.excluded_products || [];

        const nestedCategories = buildNestedProductList(
          includedCategories,
          excludedCategories,
        );

        // Collect all selectable leaf IDs (variant IDs for has_variant, else product ID)
        const includedProductIds = nestedCategories.flatMap((category) =>
          (category.products || []).flatMap((product) =>
            product.has_variant && product.variants?.length
              ? product.variants.map((v) => v.id)
              : [product.id],
          ),
        );

        setState((draft) => {
          draft.assign.allCategoryProductList = nestedCategories;
          draft.assign.allProducts = nestedCategories.flatMap(
            (category) => category.products || [],
          );
          draft.assign.filteredProducts = nestedCategories;
          draft.assign.includedProductIds = includedProductIds;
          draft.assign.productList = filterWithIncludedProducts(
            draft.assign.productList,
            includedProductIds,
          );
          draft.assign.selectedProducts = draft.assign.productList.map(
            (item) => item.id,
          );
          draft.assign.isModalLoading = false;
        });
      } else {
        setState((draft) => {
          draft.assign.isModalLoading = false;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to load loyalty card products",
      );
      setState((draft) => {
        draft.assign.isModalLoading = false;
      });
    }
  };

  useEffect(() => {
    getPointPromotionProducts();
  }, [promotionId, promotionData]);

  useEffect(() => {
    if (promotionId && promotionData?.card_type) {
      getLoyaltyIncludedProducts();
    }
  }, [promotionId, promotionData?.card_type]);

  const openAssignModal = () => {
    setState((draft) => {
      draft.isOpen = true;
      draft.assign.filteredProducts = draft.assign.allCategoryProductList;
      draft.assign.selectedProducts = (draft.assign.productList || []).map(
        (item) => item.id,
      );
    });
  };

  const closeAssignModal = () => {
    setState((draft) => {
      draft.isOpen = false;
    });
  };

  const HandleFilterProduct = (type, text) => {
    const ProductList = state.assign.allCategoryProductList;

    if (type === "initial") {
      setState((draft) => {
        draft.assign.filteredProducts = ProductList;
      });
      return;
    }

    const Search = (data, searchText) => {
      const normalized = searchText.toLowerCase();
      return data
        .map((val) => {
          const filtered = val.products
            .map((product) => {
              if (product.name.toLowerCase().includes(normalized)) return product;
              if (product.has_variant && product.variants?.length) {
                const matchedVariants = product.variants.filter((v) =>
                  v.name.toLowerCase().includes(normalized),
                );
                if (matchedVariants.length)
                  return { ...product, variants: matchedVariants };
              }
              return null;
            })
            .filter(Boolean);
          return filtered.length ? { ...val, products: filtered } : undefined;
        })
        .filter(Boolean);
    };

    setState((draft) => {
      draft.assign.filteredProducts = Search(ProductList, text || "");
    });
  };

  const handleProductSelect = (type, value, event) => {
    // Returns selectable leaf IDs: variant IDs for has_variant products, else product ID
    const getLeafIds = (products = []) =>
      products.flatMap((product) =>
        product.has_variant && product.variants?.length
          ? product.variants.map((v) => v.id)
          : [product.id],
      );

    if (type === "all") {
      if (event.target.checked) {
        setState((draft) => {
          draft.assign.selectedProducts = value.flatMap((cat) =>
            getLeafIds(cat.products),
          );
        });
      } else {
        setState((draft) => {
          draft.assign.selectedProducts = [];
        });
      }
      return;
    }

    if (type === "body") {
      if (event.target.checked) {
        setState((draft) => {
          draft.assign.selectedProducts = [...draft.assign.selectedProducts, value.id];
        });
      } else {
        setState((draft) => {
          draft.assign.selectedProducts = draft.assign.selectedProducts.filter(
            (val) => val !== value.id,
          );
        });
      }
      return;
    }

    const subRowsID = value.variants?.length
      ? value.variants.map((v) => v.id)   // parent-product level
      : getLeafIds(value.products || []);  // category level

    if (event.target.checked) {
      setState((draft) => {
        draft.assign.selectedProducts = [
          ...new Set([...draft.assign.selectedProducts, ...subRowsID]),
        ];
      });
    } else {
      setState((draft) => {
        draft.assign.selectedProducts = draft.assign.selectedProducts.filter(
          (val) => !subRowsID.includes(val),
        );
      });
    }
  };

  const handleAssignProduct = async () => {
    const payload = {
      product_ids: state.assign.selectedProducts,
    };

    if (!promotionId) return;

    setState((draft) => {
      draft.assign.isSaveButtonBusy = true;
    });

    try {
      const res = await assignProductToPointPromotionApi(promotionId, payload);
      if (res?.success) {
        successMessage(res.message || "Products assigned successfully");
        setState((draft) => {
          draft.assign.isSaveButtonBusy = false;
          draft.isOpen = false;
        });
        getPointPromotionProducts();
      } else {
        setState((draft) => {
          draft.assign.isSaveButtonBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to assign products");
      setState((draft) => {
        draft.assign.isSaveButtonBusy = false;
      });
    }
  };

  const handleSelectAllProducts = (checked) => {
    if (checked) {
      setState((draft) => {
        draft.assign.selectedProducts = draft.assign.filteredProducts.flatMap(
          (item) =>
            (item.products || []).flatMap((product) =>
              product.has_variant && product.variants?.length
                ? product.variants.map((v) => v.id)
                : [product.id],
            ),
        );
      });
    } else {
      setState((draft) => {
        draft.assign.selectedProducts = [];
      });
    }
  };

  const DeleteModals = (open, id = null) => {
    setState((draft) => {
      draft.isDelete = open;
      draft.productId = id;
    });
  };

  const handleDeleteProduct = async () => {
    if (!promotionId || !state.productId) return;

    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      const res = await deletePointPromotionProductApi(promotionId, state.productId);
      if (res?.success) {
        successMessage(res.message || "Product removed successfully");
        setState((draft) => {
          draft.assign.productList = draft.assign.productList.filter(
            (item) => item.id !== state.productId,
          );
          draft.assign.selectedProducts = draft.assign.selectedProducts.filter(
            (id) => id !== state.productId,
          );
          draft.isDelete = false;
          draft.productId = null;
          draft.isBusy = false;
        });
      } else {
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to delete product");
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  const applyToAllProducts = async () => {
    if (!promotionId) return;

    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      const res = await assignProductToPointPromotionApi(promotionId, {
        select_all: true,
      });

      if (res?.success) {
        successMessage(res.message || "Applied to all products successfully");
        handleApplyAllConfirmModal(false);
        await getPointPromotionProducts();
        setState((draft) => {
          draft.isBusy = false;
        });
      } else {
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to apply to all products",
      );
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  const handleApplyAllConfirmModal = (isOpen) => {
    setState((draft) => {
      draft.isApplyAllConfirmOpen = isOpen;
    });
  };

  return {
    state,
    openAssignModal,
    closeAssignModal,
    HandleFilterProduct,
    handleProductSelect,
    handleAssignProduct,
    handleSelectAllProducts,
    DeleteModals,
    handleDeleteProduct,
    applyToAllProducts,
    handleApplyAllConfirmModal,
  };
};
